// The debugger (docs/ONLINE_IDE_FEASIBILITY.md, Phase 4). With Debug on, Run
// runs a debug build that serves the debugger (web/runtime/web_debug_agent.h)
// through the run frame (runner.js): the program stops at the breakpoints set
// in the editor's margin, at a Stop statement and at a runtime error, or when
// Pause is pressed. It then shows where it stopped, the call stack and the
// variables, which expand, and continues or steps a line at a time.
//
// The commands and events are those of the Apple debugger's protocol
// (apple/runtime/debug_agent.h).

import { MEMORY_CAPABILITY } from "./memory-report.js";
import { setupMemoryView } from "./memory-view.js";

const REASONS = {
  breakpoint: "at a breakpoint",
  step: "after a step",
  pause: "because you paused it",
  stop: "at a Stop statement",
  error: "at a runtime error",
};

// show(visible) shows the debugger, a tab beside the console, while a
// program is stopped, and takes its tab away when the debug run ends.
export function setupDebugger({ $, editor, runner, projectPath, openAt, write, status, onError = () => {},
  show = () => {} }) {
  let session = null;         // { stopped: the stopped event, or null, frame: the frame shown }
  let pendingChildren = new Map();
  let requestSerial = 0, pendingMemory = null, memoryTimer = null;
  const invalidateMemory = () => {
    pendingMemory = null;
    clearTimeout(memoryTimer);
    memoryTimer = null;
  };
  const memory = setupMemoryView({ root: $("debug-memory"), status,
    capture: () => {
      if (!session?.stopped || !session.memorySupported || session.stopped.reason === "error") throw new Error("Pause a supported debug run outside a runtime error before capturing memory.");
      const requestId = String(++requestSerial);
      pendingMemory = requestId;
      memoryTimer = setTimeout(() => {
        if (pendingMemory !== requestId) return;
        invalidateMemory();
        memory.error("Memory capture timed out. The previous report remains available.");
      }, 10000);
      send(`memory\t${requestId}`);
    },
    exportReport: (json, filename) => {
      const url = URL.createObjectURL(new Blob([json], { type: "application/json;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });

  const button = (id, action) => $(id).addEventListener("click", () => { action(); editor.focus(); });

  // The command that sets the breakpoints: each line and file of the project.
  const breakpointsCommand = () => {
    const fields = ["breakpoints"];
    for (const [path, rows] of editor.breakpoints()) {
      for (const row of rows) fields.push(String(row), path);
    }
    return fields.join("\t");
  };

  const send = (line) => runner.debugCommand(line);

  const setControls = () => {
    const stopped = !!(session && session.stopped);
    // The program cannot go on from a runtime error: continuing ends it.
    const failed = stopped && session.stopped.reason === "error";
    // Pause in the toolbar while a debug run runs; Continue while it is
    // stopped. Their pictures are the desktop debugger's: its red light
    // pauses, its green light goes on, and its mushroom cloud ends.
    $("pause").hidden = !session;
    $("pause").textContent = failed ? "End program" : stopped ? "Continue" : "Pause";
    $("pause").dataset.icon = failed ? "debug-end" : stopped ? "debug-run" : "debug-stop";
    $("pause").title = stopped ? `${failed ? "End the program" : "Continue"} (F5)` :
      "Pause: stop the program where it is, to see its variables";
    $("debug-continue").textContent = failed ? "End program" : "Continue";
    $("debug-continue").dataset.icon = failed ? "debug-end" : "debug-run";
    $("debug-continue").title = failed ? "End the program (F5)" : "Continue: run on to the next breakpoint (F5)";
    $("debug-continue").disabled = !stopped;
    for (const id of ["debug-step-over", "debug-step-into"]) $(id).disabled = !stopped || failed;
    $("debug-step-out").disabled = !stopped || failed || session.stopped.frames.length < 2;
    memory.setSession({ active: !!session, paused: stopped, supported: !!session?.memorySupported, pending: !!pendingMemory, safe: !failed });
  };

  const text = (tag, className, content) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  };

  // A variable: its name, value and type, and its fields or elements, which
  // the program sends when it is expanded.
  const variableRow = (variable) => {
    const item = text("li", "variable");
    const line = text("div", "variable-line");
    const toggle = text("button", "toggle", variable.ref ? "▸" : "");
    toggle.type = "button";
    toggle.disabled = !variable.ref;
    toggle.setAttribute("aria-label", variable.ref ? `Show what ${variable.name} holds` : "");
    if (!variable.ref) toggle.tabIndex = -1;
    const name = text("span", "name", variable.name);
    const value = text("span", "value", variable.value);
    const type = text("span", "type", variable.type);
    line.append(toggle, name, value, type);
    item.append(line);
    item.dataset.name = variable.name;
    if (variable.ref) {
      let children = null;
      const expand = () => {
        if (children) {
          children.hidden = !children.hidden;
          toggle.textContent = children.hidden ? "▸" : "▾";
          return;
        }
        children = text("ul", "variables");
        children.append(text("li", "muted", "…"));
        item.append(children);
        toggle.textContent = "▾";
        pendingChildren.set(variable.ref, children);
        send(`expand\t${variable.ref}`);
      };
      toggle.addEventListener("click", expand);
      name.addEventListener("click", expand);
    }
    return item;
  };

  const variableList = (variables) => {
    const list = text("ul", "variables");
    if (!variables.length) list.append(text("li", "muted", "None"));
    for (const variable of variables) list.append(variableRow(variable));
    return list;
  };

  const section = (title, variables, open) => {
    const details = text("details", "debug-section");
    details.open = open;
    details.dataset.section = title;
    details.append(text("summary", "", `${title} (${variables.length})`), variableList(variables));
    return details;
  };

  // Shows a frame of the stopped program: its line, and its variables.
  const showFrame = (index) => {
    const stopped = session.stopped;
    session.frame = index;
    const frame = stopped.frames[index];
    for (const item of $("debug-frames").children) item.classList.toggle("selected", item.dataset.frame === String(index));
    const view = $("debug-variables");
    const open = new Map([...view.querySelectorAll("details")].map((details) => [details.dataset.section, details.open]));
    view.replaceChildren();
    if (frame) {
      const title = index === 0 ? "Main program" : `Locals of ${frame.function}`;
      view.append(section(title, frame.variables, open.get(title) ?? true));
    }
    view.append(section("Globals", stopped.globals, open.get("Globals") ?? true),
      section("Constants", stopped.constants, open.get("Constants") ?? false));
    const file = frame ? frame.file : stopped.file, line = frame ? frame.line : stopped.line;
    const path = projectPath(file);
    if (path) {
      openAt(path, line).then(() => editor.showStopped(path, line));
    }
  };

  const describe = (stopped) => {
    const path = projectPath(stopped.file) || stopped.file;
    const where = path ? `${path}, line ${stopped.line}` : `line ${stopped.line}`;
    const why = REASONS[stopped.reason] || `(${stopped.reason})`;
    return stopped.reason === "error" ? `Stopped at a runtime error in ${where}: ${stopped.message}` :
      `Stopped ${why} in ${where}.`;
  };

  const showStopped = (stopped) => {
    session.stopped = stopped;
    pendingChildren = new Map();
    document.body.classList.add("debugging");
    show(true);
    $("debug-state").textContent = describe(stopped);
    $("debug-state").classList.toggle("error", stopped.reason === "error");
    const frames = $("debug-frames");
    frames.replaceChildren();
    // The innermost frame first, as call stacks read.
    for (let index = stopped.frames.length - 1; index >= 0; --index) {
      const frame = stopped.frames[index];
      const item = text("li", "");
      item.dataset.frame = String(index);
      const link = text("button", "", index === 0 ? "Main program" : frame.function);
      link.type = "button";
      link.append(" ", text("span", "muted", `${projectPath(frame.file) || frame.file}:${frame.line}`));
      link.addEventListener("click", () => showFrame(index));
      item.append(link);
      frames.append(item);
    }
    showFrame(stopped.frames.length - 1);
    if (stopped.reason === "error") onError(stopped);
    setControls();
    status(stopped.reason === "error" ? "The program stopped with an error. See its variables, then end it (F5)." :
      "The program is stopped. Continue (F5), step over (F10), step into (F11) or step out (Shift+F11).");
  };

  const resume = (command) => {
    if (!session || !session.stopped) return false;
    if (session.stopped.reason === "error") command = "continue";
    session.stopped = null;
    invalidateMemory();
    editor.showStopped(null);
    $("debug-state").textContent = "Running.";
    $("debug-state").classList.remove("error");
    setControls();
    send(command);
    return true;
  };

  button("debug-continue", () => resume("continue"));
  button("pause", () => {
    if (session && session.stopped) resume("continue");
    else if (session) send("pause");
  });
  button("debug-step-over", () => resume("stepover"));
  button("debug-step-into", () => resume("stepinto"));
  button("debug-step-out", () => resume("stepout"));

  const api = {
    get active() { return !!session; },
    get stopped() { return session && session.stopped; },
    // A debug run starts; stopAtStart stops it at its first line.
    start({ stopAtStart = false } = {}) {
      invalidateMemory();
      memory.reset();
      session = { stopped: null, frame: 0, stopAtStart, memorySupported: false };
      pendingChildren = new Map();
      $("debug-state").textContent = "Starting…";
      $("debug-state").classList.remove("error");
      $("debug-frames").replaceChildren();
      $("debug-variables").replaceChildren();
      setControls();
    },
    // The program's events.
    event(event) {
      if (!session || !event) return;
      switch (event.event) {
      case "hello":
        session.memorySupported = event.capabilities?.[MEMORY_CAPABILITY] === 1;
        send(breakpointsCommand());
        send(session.stopAtStart ? "stepinto" : "run");
        $("debug-state").textContent = "Running.";
        setControls();
        break;
      case "stopped":
        showStopped(event);
        break;
      case "running":
        if (session.stopped) api.running();
        break;
      case "children": {
        const list = pendingChildren.get(event.ref);
        pendingChildren.delete(event.ref);
        if (list) list.replaceWith(variableList(event.variables));
        break;
      }
      case "memory": {
        // The runner associates events with its private MessagePort and frame.
        // Request IDs never repeat, including after End or a new run.
        if (!session.stopped || typeof event.requestId !== "string" || event.requestId !== pendingMemory) break;
        invalidateMemory();
        try {
          if (typeof event.error === "string" && !Object.hasOwn(event, "snapshot")) {
            if (event.error.length > 2048) throw new Error("Invalid memory capture error.");
            memory.error(event.error);
          } else {
            if (Object.hasOwn(event, "error") || event.snapshot?.domain !== "main" || event.snapshot?.captureState !== "paused") {
              throw new Error("Invalid memory capture reply: expected a paused main context.");
            }
            memory.accept(event.snapshot);
          }
        } catch (error) { memory.error(error); }
        setControls();
        break;
      }
      case "log":
        write(event.text, "note");
        break;
      case "fault":
        // An error the program cannot stop at, such as the browser's own
        // stack overflowing: it ends.
        onError(event);
        invalidateMemory();
        memory.setSession({ active: false, paused: false, pending: false });
        break;
      case "exit":
        invalidateMemory();
        memory.setSession({ active: false, paused: false, pending: false });
        break;
      }
    },
    running() {
      if (!session) return;
      invalidateMemory();
      session.stopped = null;
      editor.showStopped(null);
      setControls();
    },
    // F5, F10, F11 and Shift+F11 while stopped. Returns whether taken.
    key(key) {
      if (!session || !session.stopped) return false;
      return resume({ F5: "continue", F10: "stepover", F11: "stepinto", "Shift-F11": "stepout" }[key]);
    },
    // The editor's breakpoints changed: a running program takes them at once.
    breakpointsChanged() {
      if (session) send(breakpointsCommand());
    },
    // The program ended, or was stopped.
    ended() {
      invalidateMemory();
      session = null;
      pendingChildren = new Map();
      editor.showStopped(null);
      document.body.classList.remove("debugging");
      show(false);
      setControls();
    },
    // A release run also starts a new history generation.
    newRun() { api.ended(); memory.reset(); },
  };
  setControls();
  return api;
}
